import { useCallback, useEffect, useState } from "react";
import FocusLock from "react-focus-lock";
import { Controller, useForm } from "react-hook-form";
import { LockIcon, MailIcon, TicketIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import { validateEmail } from "@/domains/auth/helpers/validateEmail";
import AuthTitle from "@/domains/auth/components/AuthTitle";
import AuthServerRulesModal from "@/domains/auth/components/sign-up/AuthServerRulesModal";

interface AuthSignUpForm {
  email: string;
  password: string;
  passwordRepeat: string;
  promoCode: string;
  acceptRules: boolean;
  ageConfirm: boolean;
  subscribeConfirm: boolean;
}

type Props = {
  onSubmit: (data: AuthSignUpForm) => void;
  error?: string;
  pending: boolean;
  onCheckEmail: (email: string) => void;
  errorEmailCheck: string;
  isChecking: boolean;
};

export default function AuthSignUp({
  onSubmit,
  onCheckEmail,
  errorEmailCheck,
  isChecking,
  error = "",
  pending,
}: Props) {
  const [showRules, setShowRules] = useState(false);
  const {
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm<AuthSignUpForm>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      passwordRepeat: "",
      promoCode: "",
      acceptRules: false,
      ageConfirm: false,
      subscribeConfirm: true,
    },
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const openRulesHandler = useCallback(() => {
    setShowRules(true);
  }, []);

  const closeRulesHandler = useCallback(() => {
    setShowRules(false);
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "password" && watch("passwordRepeat")) {
        trigger("passwordRepeat");
      }
      if (name === "email" && !isChecking) {
        const errorMessage = validateEmail(value.email);
        if (!errorMessage) {
          onCheckEmail(value.email);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, trigger, isChecking, onCheckEmail]);

  return (
    <>
      {showRules && <AuthServerRulesModal onClose={closeRulesHandler} />}
      <AuthTitle>Регистрация</AuthTitle>
      <FocusLock className="flex flex-col grow">
        <form
          noValidate
          onSubmit={handleSubmit((data) => {
            if (!errors.password && !errors.passwordRepeat) {
              onSubmit(data);
            }
          })}
          tabIndex="0"
          className="gap-3.5 grow flex flex-col outline-none"
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Введите свой email",
              validate: (value) => {
                const errorMessage = validateEmail(value);
                if (errorEmailCheck) return "Данный E-mail уже используется";
                if (errorMessage) return errorMessage;

                return true;
              },
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                type="email"
                placeholder="Введите свой email"
                title="E-mail:"
                icon={<MailIcon size={18} />}
                invalid={errors.email?.message || ""}
                className="text-center [&_.control-status]:absolute"
                required
                loading={isChecking}
              />
            )}
          />
          <div className="grid gap-2.5">
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Пароль не указан",
                minLength: {
                  value: 6,
                  message: "Минимальная длина пароля 6 символов",
                },
                maxLength: {
                  value: 32,
                  message: "Максимальная длина пароля 32 символа",
                },
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  type="password"
                  placeholder="Придумайте пароль"
                  title="Пароль:"
                  icon={<LockIcon size={16} />}
                  className="text-center"
                  invalid={errors.password?.message || ""}
                  required
                  passwordVisible={isPasswordVisible}
                  onChangePasswordVisible={setIsPasswordVisible}
                />
              )}
            />
            <Controller
              name="passwordRepeat"
              control={control}
              rules={{
                required: "Пароль не указан",
                minLength: {
                  value: 6,
                  message: "Минимальная длина пароля 6 символов",
                },
                maxLength: {
                  value: 32,
                  message: "Максимальная длина пароля 32 символа",
                },
                validate: (passwordRepeat) => {
                  return watch("password") === passwordRepeat || "Пароли не совпадают";
                },
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  type="password"
                  placeholder="Повторите пароль"
                  title="Повторите пароль:"
                  hiddenTitle
                  icon={<LockIcon size={16} />}
                  invalid={errors.passwordRepeat?.message || ""}
                  className="text-center [&_.control-status]:absolute"
                  required
                  passwordVisible={isPasswordVisible}
                  onChangePasswordVisible={setIsPasswordVisible}
                />
              )}
            />
          </div>
          <Controller
            name="promoCode"
            control={control}
            rules={{
              minLength: {
                value: 6,
                message: "Минимальная длина промокода 6 символов",
              },
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                type="text"
                placeholder="Минимальное кол-во символов 4"
                title="Промокод:"
                icon={<TicketIcon size={18} />}
                className="text-center [&_.control-status]:absolute"
                invalid={errors.promoCode?.message || ""}
                info={
                  <div
                    className="[&_span]:text-accent"
                    dangerouslySetInnerHTML={{
                      __html: `Введите личный промокод пригласившего и получите награду по достижении <span><b>3</b> уровня</span> любым персонажем`,
                    }}
                  />
                }
              />
            )}
          />
          <div className="grid gap-2.5 mt-2">
            <Controller
              name="acceptRules"
              control={control}
              rules={{ required: "Примите правила" }}
              render={({ field }) => (
                <div>
                  <label className="flex items-center text-14 gap-2.5">
                    <input {...field} type="checkbox" className="checkbox" checked={field.value} />
                    <div>
                      Я ознакомлен(а){" "}
                      <button
                        type="button"
                        className="text-accent underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRulesHandler();
                        }}
                      >
                        с правилами сервера
                      </button>{" "}
                      и принимаю их.
                      <span className="text-danger">*</span>
                    </div>
                  </label>
                  {errors.acceptRules && (
                    <div className="control-status control-status--error">
                      *{errors.acceptRules.message}
                    </div>
                  )}
                </div>
              )}
            />
            <Controller
              name="ageConfirm"
              control={control}
              rules={{ required: "Подтвердите возраст" }}
              render={({ field }) => (
                <div>
                  <label className="flex items-center text-14 gap-2.5">
                    <input {...field} type="checkbox" className="checkbox" checked={field.value} />
                    <div>
                      Я подтверждаю, что мне есть 18 лет <span className="text-danger">*</span>
                    </div>
                  </label>
                  {errors.ageConfirm && (
                    <div className="control-status control-status--error">
                      *{errors.ageConfirm.message}
                    </div>
                  )}
                </div>
              )}
            />
            <Controller
              name="subscribeConfirm"
              control={control}
              render={({ field }) => (
                <label className="flex items-center text-14 gap-2.5">
                  <input {...field} type="checkbox" className="checkbox" checked={field.value} />
                  <div>
                    Я желаю получать информационные рассылки с уникальными акциями и предложениями
                  </div>
                </label>
              )}
            />
          </div>
          <div className="mt-auto grid gap-2.5">
            {!!error && <div className="alert alert--danger">*{error}</div>}
            <Button submit className="btn--md w-full" loading={pending}>
              Зарегистрироваться
            </Button>
          </div>
        </form>
      </FocusLock>
    </>
  );
}
